<template>
  <b-container class="pt-3 pb-5">
    <overlay-spinner :loading="isLoading"/>
    <h2>Downloads</h2>
    <custom-section source="Downloads"/>

    <b-form-group
      class="my-2"
      invalid-feedback="No files found matching filter"
      :state="(fileFilter === '' || publishedFiles.length > 0 || sharedFiles.length > 0) ? null : false">
      <b-form-input
        autofocus type="text"
        v-model="fileFilter"
        debounce="500" trim
        placeholder="Enter part of file name or description"
      />
    </b-form-group>

    <b-tabs v-if="files && files.length > 0" content-class="mt-3">
      <b-tab
        active
        v-if="publishedFiles && publishedFiles.length > 0"
        :title="`Published files (${publishedFiles.length})`">
        <section>
          <ul>
            <li v-for="(item, index) in publishedFiles" :key="index">
              <b-link
                @click="download(item.filename)"
                class="py-0 align-baseline">
                {{item | fileAsLinkText}}
              </b-link>
              <span :title="item.createAt | timestampAsDate">(created {{ item.createAt | fromNow }})</span>
            </li>
          </ul>
        </section>
      </b-tab>
      <b-tab v-if="sharedFiles && sharedFiles.length > 0" :title="`Shared or owned files (${sharedFiles.length})`">
        <section>
          <ul>
            <li v-for="(item, index) in sharedFiles" :key="index">
              <b-link
                @click="download(item.filename)"
                class="py-0 px-1 align-baseline">
                {{ item | fileAsLinkText }}
              </b-link>
              <span
                :title="item.createAt | timestampAsDate">(created {{ item.createAt | fromNow }} by {{item.author}})</span>
            </li>
          </ul>
        </section>
      </b-tab>
    </b-tabs>
    <b-alert v-else :show="!downloading" variant="info">
      There are no files available to download
    </b-alert>

    <b-button-toolbar class="mt-2 mb-5">
      <b-dropdown :disabled="isDownloadingRefs" variant="outline-secondary">
        <template v-slot:button-content>
          <b-spinner v-if="isDownloadingRefs" small/>
          Download reference data
        </template>
        <b-dropdown-item @click="onReferenceClick()">As Excel file</b-dropdown-item>
        <b-dropdown-item @click="onReferenceClick('application/json')">As JSON</b-dropdown-item>
      </b-dropdown>
    </b-button-toolbar>

    <b-modal ref="downloadModal" title="Download">
      <overlay-spinner :loading="downloading.isBusy"/>
      <p>You are going to download <b>{{downloading.filename}}</b></p>
      <b-form-group v-if="$auth.isGuest" label="Email address:">
        <b-form-input
          type="email" v-model="downloading.email"
          required placeholder="Enter email">
        </b-form-input>
      </b-form-group>
      <b-button-toolbar slot="modal-footer">
        <b-button
          v-if="$auth.isRegistered"
          :disabled="downloading.isBusy"
          @click="onFetchDownload">
          Download
        </b-button>
        <b-button
          v-if="$auth.isGuest"
          :disabled="!downloading.email || downloading.isBusy"
          @click="onRequestDownload">
          Request download
        </b-button>
      </b-button-toolbar>
    </b-modal>
  </b-container>
</template>

<script>
  import {mapActions, mapMutations, mapState} from 'vuex';
  import {mapCacheActions} from 'vuex-cache';
  import {NOTIFY} from '../../store/mutation-types';
  import OverlaySpinner from '../OverlaySpinner';
  import CustomSection from './CustomSection';
  import {download} from '../../utilities/download';
  import saveAs from 'file-saver';
  import moment from 'moment';

  export default {
    name: 'Downloads',
    components: {OverlaySpinner, CustomSection},
    data() {
      return {
        downloading: {filename: '', email: '', isBusy: false},
        isDownloadingRefs: false,
        fileFilter: '',
      };
    },
    filters: {
      fileAsLinkText(file) {
        if (file.description && file.description.trim().length > 0) return file.description;
        return file.filename;
      },
      timestampAsDate: ts => moment(ts).format('DD-MM-YYYY HH:mm'),
      fromNow: value => value ? moment(value).fromNow() : '',
    },
    methods: {
      ...mapMutations({notify: NOTIFY}),
      ...mapActions(['fetchFile', 'requestFile', 'fetchRefs']),
      ...mapCacheActions(['fetchFiles']),
      download(filename) {
        this.downloading.filename = filename;
        this.$refs.downloadModal.show();
      },
      async onFetchDownload() {
        const {filename} = this.downloading;
        this.downloading.isBusy = true;
        const {directLink} = await this.fetchFile({filename});
        download(directLink, filename);
        this.downloading.isBusy = false;
        this.$refs.downloadModal.hide();
      },
      async onRequestDownload() {
        const {filename, email} = this.downloading;
        this.downloading.isBusy = true;
        const result = await this.requestFile({filename, email});
        this.notify({message: result.message, variant: 'info', countDown: 10});
        this.downloading.isBusy = false;
        this.$refs.downloadModal.hide();
      },
      async onReferenceClick(format) {
        this.isDownloadingRefs = true;
        const blob = await this.fetchRefs({format});
        saveAs(blob, `reference.${format === 'application/json' ? 'json' : 'xlsx'}`);
        this.isDownloadingRefs = false;
      },
      filterFile(isPublic, filter) {
        filter = filter.toLowerCase();
        return file => !file.author === isPublic && (filter === '' ||
          (file.description && file.description.toLowerCase().indexOf(filter) >= 0) ||
          file.filename.toLowerCase().indexOf(filter) >= 0);
      },
    },
    computed: {
      ...mapState({
        files: state => state.data.files,
        isLoading: state => state.loading.files,
      }),
      publishedFiles() {
        const {files, filterFile, fileFilter} = this;
        const publishedFiles = files.filter(filterFile(true, fileFilter));
        publishedFiles.sort((f1, f2) => f2.createAt - f1.createAt);
        return publishedFiles;
      },
      sharedFiles() {
        const {files, filterFile, fileFilter} = this;
        const ownedFiles = files.filter(filterFile(false, fileFilter));
        ownedFiles.sort((f1, f2) => f2.createAt - f1.createAt);
        return ownedFiles;
      },
    },
    mounted() {
      this.fetchFiles({includePublic: true, includePrivate: true});
    },
  };
</script>
